/**
 * Canonical reachability semantics (user-facing status).
 * Property status reflects telemetry reality first; auth failures only matter when there is no recent telemetry.
 * last_poll_at / last_poll_status remain for internal diagnostics (admin, logs).
 */

export const POLL_STALE_MINUTES = 15;
export const POLL_OFFLINE_MINUTES = 30;

/** No successful provider contact has ever been recorded. */
export const NEVER_POLLED = 'never' as const;

export type PollStatus = 'live' | 'stale' | 'offline' | 'never' | 'error';

/** Age of a timestamp in minutes from now. */
export function getTimestampAgeMinutes(iso: string): number {
  const ts = new Date(iso).getTime();
  return (Date.now() - ts) / (1000 * 60);
}

/**
 * Canonical reachability status for UI (Fleet, Property dashboard, Map).
 * Priority 1: Use actual telemetry timestamp if available (property is online if telemetry within 15 min).
 * Priority 2: If no telemetry, auth failure more recent than last success → offline.
 * Priority 3: Fallback to last_provider_ok_at age.
 * - live: last telemetry or last success ≤ 15 min ago (and no auth block when using fallback)
 * - stale: 15–30 min ago
 * - offline: >30 min ago OR auth failure when no recent telemetry
 * - never: no last_provider_ok_at and no lastTelemetryAt
 */
export function getReachabilityStatus(
  lastTelemetryAt: string | null,
  lastProviderOkAt: string | null,
  lastProviderAuthFailedAt: string | null
): PollStatus {
  if (lastTelemetryAt) {
    const age = getTimestampAgeMinutes(lastTelemetryAt);
    if (age <= POLL_STALE_MINUTES) return 'live';
    if (age <= POLL_OFFLINE_MINUTES) return 'stale';
    return 'offline';
  }

  if (
    lastProviderAuthFailedAt &&
    (!lastProviderOkAt || lastProviderAuthFailedAt > lastProviderOkAt)
  ) {
    return 'offline';
  }

  if (lastProviderOkAt) {
    const age = getTimestampAgeMinutes(lastProviderOkAt);
    if (age <= POLL_STALE_MINUTES) return 'live';
    if (age <= POLL_OFFLINE_MINUTES) return 'stale';
    return 'offline';
  }

  return 'never';
}

/**
 * Legacy: derives status from last_poll_at / last_poll_status (internal diagnostics only).
 * Do not use for user-facing status badge; use getReachabilityStatus instead.
 */
export function getPollStatus(
  lastPollAt: string | null,
  lastPollStatus: string | null
): PollStatus {
  if (!lastPollAt) return 'never';
  if (lastPollStatus !== 'success') return 'error';
  const now = Date.now();
  const polled = new Date(lastPollAt).getTime();
  const diffMinutes = (now - polled) / (1000 * 60);
  if (diffMinutes <= POLL_STALE_MINUTES) return 'live';
  if (diffMinutes <= POLL_OFFLINE_MINUTES) return 'stale';
  return 'offline';
}

/**
 * Maps PollStatus to legacy { online, dataStale } for fleet/readiness.
 * live -> online true, dataStale false; stale -> online false, dataStale true; else -> both false.
 */
export function pollStatusToOnlineDataStale(status: PollStatus): { online: boolean; dataStale: boolean } {
  switch (status) {
    case 'live':
      return { online: true, dataStale: false };
    case 'stale':
      return { online: false, dataStale: true };
    default:
      return { online: false, dataStale: false };
  }
}
